class CollectionListSlider {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-clsr-track]').parentElement;
    this.track = root.querySelector('[data-clsr-track]');
    this.prevBtn = root.querySelector('[data-clsr-prev]');
    this.nextBtn = root.querySelector('[data-clsr-next]');
    this.originalSlides = Array.from(this.track.children);

    this.loop = root.dataset.loop === 'true';
    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 4000;
    this.pauseOnHover = root.dataset.pauseHover === 'true';

    this.index = 0;
    this.timer = null;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;

    this.init();
  }

  getPerView() {
    const width = window.innerWidth;
    const styles = getComputedStyle(this.root);
    if (width >= 990) return parseInt(styles.getPropertyValue('--clsr-desktop-cols'), 10) || 5;
    if (width >= 750) return parseInt(styles.getPropertyValue('--clsr-tablet-cols'), 10) || 3;
    return parseInt(styles.getPropertyValue('--clsr-mobile-cols'), 10) || 1;
  }

  init() {
    if (this.loop && this.originalSlides.length > 1) {
      this.setupLoopClones();
    }

    this.slides = Array.from(this.track.children);
    this.slideCount = this.originalSlides.length;

    if (this.loop && this.slideCount > 1) {
      this.index = this.slideCount;
      this.jumpTo(this.index, false);
    }

    this.bindEvents();
    this.updateArrowState();

    if (this.autoplay && this.slideCount > 1) {
      this.startAutoplay();
    }

    window.addEventListener('resize', this.debounce(() => {
      this.jumpTo(this.index, false);
    }, 150));
  }

  setupLoopClones() {
    const perView = this.getPerView();
    const cloneCount = Math.max(perView, 1) + 1;

    const startClones = this.originalSlides.slice(-cloneCount).map((s) => {
      const clone = s.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    });

    const endClones = this.originalSlides.slice(0, cloneCount).map((s) => {
      const clone = s.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    });

    startClones.forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    endClones.forEach((clone) => this.track.appendChild(clone));

    this.leadingClones = startClones.length;
  }

  getSlideWidth() {
    const slide = this.track.children[0];
    if (!slide) return 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  jumpTo(index, animate = true) {
    const slideWidth = this.getSlideWidth();
    const offset = this.loop ? this.leadingClones || 0 : 0;
    const translate = -((index + offset) * slideWidth);

    this.track.style.transition = animate ? 'transform 0.45s ease' : 'none';
    this.track.style.transform = `translateX(${translate}px)`;
    this.currentTranslate = translate;
  }

  goTo(index) {
    this.index = index;
    this.jumpTo(this.index);

    if (this.loop) {
      const total = this.slideCount;
      if (this.index >= total) {
        setTimeout(() => {
          this.index = this.index - total;
          this.jumpTo(this.index, false);
        }, 460);
      } else if (this.index < 0) {
        setTimeout(() => {
          this.index = this.index + total;
          this.jumpTo(this.index, false);
        }, 460);
      }
    }

    this.updateArrowState();
  }

  next() {
    this.goTo(this.index + 1);
  }

  prev() {
    this.goTo(this.index - 1);
  }

  updateArrowState() {
    if (this.loop) return;
    const perView = this.getPerView();
    const maxIndex = Math.max(this.slideCount - perView, 0);
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= maxIndex;
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  bindEvents() {
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.next();
        if (this.autoplay) this.startAutoplay();
      });
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prev();
        if (this.autoplay) this.startAutoplay();
      });
    }

    if (this.pauseOnHover && this.autoplay) {
      this.root.addEventListener('mouseenter', () => this.stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
    }

    // Touch / swipe support
    let startX = 0;
    let isDown = false;

    this.viewport.addEventListener('touchstart', (e) => {
      isDown = true;
      startX = e.touches[0].clientX;
      this.stopAutoplay();
    }, { passive: true });

    this.viewport.addEventListener('touchend', (e) => {
      if (!isDown) return;
      isDown = false;
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;

      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }

      if (this.autoplay) this.startAutoplay();
    });
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

function initCollectionListSliders() {
  document.querySelectorAll('[data-clsr-slider]').forEach((el) => {
    if (!el.dataset.clsrInitialized) {
      el.dataset.clsrInitialized = 'true';
      new CollectionListSlider(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionListSliders);
} else {
  initCollectionListSliders();
}

document.addEventListener('shopify:section:load', initCollectionListSliders);
